// src/components/dental/TreatmentHistory.js
import React from 'react';
import { Table, Tag, Badge, Space, Button } from 'antd';
import { EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';

const TreatmentHistory = ({ treatmentHistory, setTreatmentHistory }) => {
  
  const handleDelete = (id) => {
    setTreatmentHistory(treatmentHistory.filter(item => item.id !== id));
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      width: 120,
    },
    {
      title: 'Tooth',
      dataIndex: 'toothNumber',
      key: 'toothNumber',
      render: (tooth) => (
        <Badge 
          count={tooth} 
          style={{ backgroundColor: '#1890ff' }} 
          overflowCount={99}
        />
      ),
      width: 80,
      align: 'center',
    },
    {
      title: 'Treatment',
      dataIndex: 'treatmentType',
      key: 'treatmentType',
      render: (type) => {
        const colorMap = {
          examination: 'blue',
          cleaning: 'green',
          filling: 'orange',
          extraction: 'red',
          crown: 'purple',
          rootCanal: 'magenta',
          bridge: 'cyan',
          implant: 'gold'
        };
        return <Tag color={colorMap[type] || 'blue'}>{type}</Tag>;
      },
      filters: [
        { text: 'Examination', value: 'examination' },
        { text: 'Cleaning', value: 'cleaning' },
        { text: 'Filling', value: 'filling' },
        { text: 'Extraction', value: 'extraction' },
        { text: 'Crown', value: 'crown' },
        { text: 'Root Canal', value: 'rootCanal' },
      ],
      onFilter: (value, record) => record.treatmentType === value,
    },
    {
      title: 'Surface',
      dataIndex: 'surface',
      key: 'surface',
      render: (surfaces) => (
        <div>
          {surfaces?.map(surface => (
            <Tag key={surface} size="small" color="geekblue">
              {surface.charAt(0).toUpperCase()}
            </Tag>
          ))}
        </div>
      ),
      width: 120,
    },
    {
      title: 'Material',
      dataIndex: 'material',
      key: 'material',
      render: (material) => material ? <Tag color="cyan">{material}</Tag> : '-',
    },
    {
      title: 'Cost',
      dataIndex: 'cost',
      key: 'cost',
      render: (cost) => cost ? `₹${cost.toLocaleString()}` : '-',
      sorter: (a, b) => (a.cost || 0) - (b.cost || 0),
      width: 100,
      align: 'right',
    },
    {
      title: 'Urgency',
      dataIndex: 'urgency',
      key: 'urgency',
      render: (urgency) => {
        const colorMap = {
          emergency: 'red',
          urgent: 'orange',
          routine: 'green'
        };
        const iconMap = {
          emergency: '🔴',
          urgent: '🟡',
          routine: '🟢'
        };
        return urgency ? (
          <Tag color={colorMap[urgency]}>
            {iconMap[urgency]} {urgency}
          </Tag>
        ) : '-';
      },
      filters: [
        { text: 'Emergency', value: 'emergency' },
        { text: 'Urgent', value: 'urgent' },
        { text: 'Routine', value: 'routine' },
      ],
      onFilter: (value, record) => record.urgency === value,
    },
    {
      title: 'Follow-up',
      dataIndex: 'followUpDate',
      key: 'followUpDate',
      render: (date, record) => {
        if (record.followUp && date) {
          const isOverdue = new Date(date) < new Date();
          return (
            <Tag color={isOverdue ? 'red' : 'blue'}>
              {isOverdue ? '⚠️ Overdue' : '📅 Scheduled'}
            </Tag>
          );
        }
        return record.followUp ? <Tag color="orange">📋 Pending</Tag> : '-';
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="link" 
            icon={<EyeOutlined />} 
            size="small"
            title="View Details"
          >
            View
          </Button>
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            size="small"
            title="Edit Treatment"
          >
            Edit
          </Button>
          <Button 
            type="link" 
            danger
            icon={<DeleteOutlined />} 
            size="small"
            title="Delete Treatment"
            onClick={() => handleDelete(record.id)}
          >
            Delete
          </Button>
        </Space>
      ),
      width: 150,
      fixed: 'right',
    }
  ];

  const expandedRowRender = (record) => {
    return (
      <div style={{ padding: '16px', backgroundColor: '#fafafa', borderRadius: '8px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <h4 style={{ marginBottom: '8px', color: '#1890ff' }}>Clinical Notes</h4>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
              {record.notes || 'No clinical notes recorded'}
            </p>
          </div>
          <div>
            <h4 style={{ marginBottom: '8px', color: '#1890ff' }}>Post-Op Instructions</h4>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
              {record.postOpInstructions || 'No special instructions'}
            </p>
          </div>
        </div>
        {record.anesthesia && (
          <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#e6f7ff', borderRadius: '4px' }}>
            <strong>Anesthesia:</strong> {record.anesthesia}
            {record.duration && <span> | <strong>Duration:</strong> {record.duration} minutes</span>}
            {record.warranty && <span> | <strong>Warranty:</strong> {record.warranty}</span>}
          </div>
        )}
      </div>
    );
  };

  const summary = (data) => {
    const totalCost = data.reduce((sum, item) => sum + (item.cost || 0), 0);
    const treatmentCount = data.length;
    const emergencyCount = data.filter(item => item.urgency === 'emergency').length;
    
    return (
      <Table.Summary.Row style={{ backgroundColor: '#f0f2f5' }}>
        <Table.Summary.Cell index={0} colSpan={5}>
          <strong>Summary</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={1} align="right">
          <strong>₹{totalCost.toLocaleString()}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2} colSpan={3}>
          <strong>{treatmentCount} treatments</strong>
          {emergencyCount > 0 && (
            <Tag color="red" style={{ marginLeft: '8px' }}>
              {emergencyCount} emergency
            </Tag>
          )}
        </Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  return (
    <div style={{ padding: '16px' }}>
      <Table 
        dataSource={treatmentHistory}
        columns={columns}
        rowKey="id"
        pagination={{ 
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} treatments`
        }}
        scroll={{ x: 1200 }}
        expandable={{
          expandedRowRender,
          rowExpandable: (record) => record.notes || record.postOpInstructions,
        }}
        summary={summary}
        size="middle"
      />
    </div>
  );
};

export default TreatmentHistory;